'use client';

import { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Play, X } from 'lucide-react';

import { isGifUrl, isVideoUrl, splitAttachments } from '@/lib/attachments';
import DocumentAttachment from './DocumentAttachment';

/* ------------------------------------------------------------------ */
/*  Thumbnail strip item                                                */
/* ------------------------------------------------------------------ */

function ThumbnailItem({
  src,
  index,
  isActive,
  onClick,
}: {
  src: string;
  index: number;
  isActive: boolean;
  onClick: () => void;
}) {
  const isVideo = isVideoUrl(src);
  const isGif = isGifUrl(src);

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Select attachment ${index + 1}`}
      aria-current={isActive}
      className={`relative shrink-0 w-20 h-20 md:w-full md:h-20 rounded-lg overflow-hidden cursor-pointer border-2 transition-all duration-150 outline-none focus-visible:ring-2 focus-visible:ring-[#554E49] focus-visible:ring-offset-2 ${
        isActive
          ? 'border-orange-500 shadow-md'
          : 'border-transparent hover:border-gray-300'
      }`}
    >
      {isVideo ? (
        <>
          <video
            src={src}
            muted
            preload="metadata"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <Play className="h-4 w-4 text-white fill-white drop-shadow" />
          </div>
        </>
      ) : (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={src}
          alt={`Attachment ${index + 1}`}
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
        />
      )}
      {isGif && (
        <span className="absolute bottom-0.5 left-0.5 rounded bg-black/60 px-1 py-0.5 text-[8px] font-bold uppercase tracking-wider text-white leading-none">
          GIF
        </span>
      )}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Main media viewer (blurred backdrop + object-contain foreground)   */
/* ------------------------------------------------------------------ */

function MainViewer({
  src,
  alt,
  hasCustomAlt,
}: {
  src: string;
  alt: string;
  hasCustomAlt?: boolean;
}) {
  const isVideo = isVideoUrl(src);
  const isGif = isGifUrl(src);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showAltPopup, setShowAltPopup] = useState(false);

  // Reset player state when src changes
  useEffect(() => {
    setIsPlaying(false);
    setShowAltPopup(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [src]);

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play().catch(() => {});
    }
  };

  if (isVideo) {
    return (
      <div className="relative w-full h-full">
        {/* Blurred backdrop */}
        <video
          src={src}
          muted
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl opacity-20 pointer-events-none"
          aria-hidden
        />
        {/* Foreground video */}
        <video
          ref={videoRef}
          src={src}
          controls={isPlaying}
          preload="metadata"
          className="relative z-10 w-full h-full object-contain"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
        />
        {!isPlaying && (
          <button
            type="button"
            aria-label="Play video"
            onClick={togglePlay}
            className="absolute inset-0 m-auto z-20 flex h-14 w-14 items-center justify-center rounded-full bg-black/60 hover:bg-black/80 text-white transition-all hover:scale-110 outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/60 shadow-lg border border-white/10"
          >
            <Play className="h-6 w-6 fill-current translate-x-0.5" aria-hidden="true" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      {/* Background image — raw, unblurred */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover scale-110"
      />
      {/* Foreground image / GIF — backdrop-blur creates the frosted effect */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        alt={alt}
        className="relative z-10 w-full h-full backdrop-blur-sm object-contain"
        decoding="async"
      />
      {isGif && (
        <span className="absolute top-2.5 left-2.5 z-20 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white select-none pointer-events-none">
          GIF
        </span>
      )}
      {hasCustomAlt && (
        <div className="absolute bottom-2.5 left-2.5 z-20">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowAltPopup((prev) => !prev);
            }}
            aria-expanded={showAltPopup}
            aria-haspopup="dialog"
            aria-controls="image-alt-popup"
            className="rounded-full bg-black/70 hover:bg-black/85 text-white text-[10px] font-bold px-2 py-0.5 backdrop-blur transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label={`View image description: ${alt}`}
            title={alt}
          >
            ALT
          </button>
          {showAltPopup && (
            <div
              id="image-alt-popup"
              role="dialog"
              aria-label="Image description"
              onClick={(e) => e.stopPropagation()}
              className="absolute bottom-7 left-0 max-w-xs w-64 p-2.5 bg-black/95 text-white text-xs rounded-xl shadow-xl border border-white/15 z-30 animate-in fade-in-50"
            >
              <div className="flex items-center justify-between mb-1 pb-1 border-b border-white/10">
                <span className="font-semibold text-white/90">Image description</span>
                <button
                  type="button"
                  onClick={() => setShowAltPopup(false)}
                  className="text-white/60 hover:text-white"
                  aria-label="Close description"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
              <p className="text-white/80 leading-snug break-words max-h-36 overflow-y-auto">
                {alt}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  PostAttachmentsCarousel — Marketplace-style gallery layout         */
/* ------------------------------------------------------------------ */

export interface PostAttachmentsCarouselProps {
  attachments: string[];
  media?: {
    id?: string;
    postID?: string;
    url: string;
    altText?: string | null;
    position?: number;
  }[] | null;
}

export default function PostAttachmentsCarousel({
  attachments,
  media,
}: PostAttachmentsCarouselProps) {
  const [current, setCurrent] = useState(0);

  const items = useMemo(() => {
    if (media && media.length > 0) {
      const sorted = [...media].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      return sorted.map((m) => ({
        url: m.url,
        altText: m.altText || null,
      }));
    }
    return (attachments || []).map((url) => ({
      url,
      altText: null,
    }));
  }, [media, attachments]);

  if (items.length === 0) return null;

  // A PDF has no thumbnail and nothing to put in a viewer, so documents are
  // pulled out and listed as files. Without this they fell through to the
  // <img> branch and rendered as a broken image.
  const { documents, media: viewable } = splitAttachments(items);

  const documentList =
    documents.length > 0 ? (
      <ul className="my-2 space-y-2">
        {documents.map((doc) => (
          <li key={doc.url}>
            <DocumentAttachment url={doc.url} />
          </li>
        ))}
      </ul>
    ) : null;

  if (viewable.length === 0) return documentList;

  // Single attachment — no chrome needed
  if (viewable.length === 1) {
    const item = viewable[0];
    return (
      <>
        <div className="my-2 overflow-hidden rounded-lg h-72 md:h-96">
          <MainViewer
            src={item.url}
            alt={item.altText || 'Post attachment'}
            hasCustomAlt={Boolean(item.altText)}
          />
        </div>
        {documentList}
      </>
    );
  }

  const total = viewable.length;
  const prev = () => setCurrent((c) => (c - 1 + total) % total);
  const next = () => setCurrent((c) => (c + 1) % total);

  const currentItem = viewable[current] || viewable[0];

  return (
    <>
    <div className="my-2 flex flex-col sm:flex-row gap-2 select-none">
      {/* Thumbnail strip — horizontal on mobile, vertical on sm+ */}
      <div className="flex flex-row sm:flex-col gap-2 overflow-x-auto sm:overflow-y-auto sm:overflow-x-hidden sm:max-h-96 sm:w-auto shrink-0 p-2 bg-orange-50 rounded-lg">
        {viewable.map((item, i) => (
          <ThumbnailItem
            key={i}
            src={item.url}
            index={i}
            isActive={current === i}
            onClick={() => setCurrent(i)}
          />
        ))}
      </div>

      {/* Main viewer */}
      <div className="relative flex-1 overflow-hidden rounded-lg h-72 md:h-96">
        <MainViewer
          key={current}
          src={currentItem.url}
          alt={currentItem.altText || `Attachment ${current + 1} of ${total}`}
          hasCustomAlt={Boolean(currentItem.altText)}
        />

        {/* Prev / Next arrows — overlaid on the main viewer */}
        {/* Navigation Arrows — always visible, matching Marketplace */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); prev(); }}
          aria-label="Previous attachment"
          className="absolute z-20 cursor-pointer left-4 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-md hover:shadow-lg transition-shadow outline-none focus-visible:ring-2 focus-visible:ring-[#554E49] focus-visible:ring-offset-2"
        >
          <ChevronLeft className="w-5 h-5 text-gray-700" />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); next(); }}
          aria-label="Next attachment"
          className="absolute z-20 cursor-pointer right-4 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-md hover:shadow-lg transition-shadow outline-none focus-visible:ring-2 focus-visible:ring-[#554E49] focus-visible:ring-offset-2"
        >
          <ChevronRight className="w-5 h-5 text-gray-700" />
        </button>

        {/* Counter badge */}
        <div
          aria-hidden="true"
          className="absolute bottom-2.5 right-2.5 z-30 rounded-full bg-black/50 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur-sm"
        >
          {current + 1} / {total}
        </div>
        {/* Changing attachment swaps the image with no spoken feedback, and
            "3 / 7" on its own reads as bare numbers. Announce it in words. */}
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          Attachment {current + 1} of {total}
        </div>
      </div>
    </div>
    {documentList}
    </>
  );
}
